import { useEffect, useRef } from "react";

export type WordPath = {
  word: string;
  cells: [number, number][];
};

type WordSearchCanvasProps = {
  rows: number;
  columns: number;
  paths: WordPath[];
  isGameStarted: boolean;
};

const COLORS = [
  "#DC2626",
  "#2563EB",
  "#16A34A",
  "#CA8A04",
  "#7C3AED",
  "#EA580C",
  "#0891B2",
  "#DB2777",
  "#4D7C0F",
  "#0D9488",
  "#C2410C",
  "#6D28D9",
  "#B91C1C",
  "#1D4ED8",
  "#15803D",
];

const WordSearchCanvas = ({
  rows,
  columns,
  paths,
  isGameStarted,
}: WordSearchCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    const ctx = canvas?.getContext("2d");

    if (!canvas || !ctx) {
      return;
    }

    const cellSizeVw = 3;
    const borderSizeVw = 0.1;

    const widthVw = columns * cellSizeVw + borderSizeVw;
    const heightVw = rows * cellSizeVw + borderSizeVw;

    // Canvas API всё равно рисует в px
    const width = (widthVw / 100) * window.innerWidth;
    const height = (heightVw / 100) * window.innerWidth;

    const cellSize = (cellSizeVw / 100) * window.innerWidth;
    const borderSize = (borderSizeVw / 100) * window.innerWidth;

    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);

    canvas.width = pixelWidth;
    canvas.height = pixelHeight;

    canvas.style.width = `${widthVw}vw`;
    canvas.style.height = `${heightVw}vw`;

    ctx.setTransform(pixelWidth / width, 0, 0, pixelHeight / height, 0, 0);
    /*
     * Сетка.
     */
    ctx.strokeStyle = "#1a6a7c";

    ctx.lineWidth = borderSize;

    ctx.beginPath();

    for (let row = 0; row <= rows; row++) {
      const y = row * cellSize + borderSize / 2;

      ctx.moveTo(borderSize / 2, y);

      ctx.lineTo(width - borderSize / 2, y);
    }

    for (let col = 0; col <= columns; col++) {
      const x = col * cellSize + borderSize / 2;

      ctx.moveTo(x, borderSize / 2);

      ctx.lineTo(x, height - borderSize / 2);
    }

    ctx.stroke();

    const segments = new Map<string, number[]>();

    paths.forEach((path, pathIndex) => {
      for (let i = 0; i < path.cells.length - 1; i++) {
        const a = path.cells[i];

        const b = path.cells[i + 1];

        const first = `${a[0]},${a[1]}`;

        const second = `${b[0]},${b[1]}`;

        const key =
          first < second ? `${first}-${second}` : `${second}-${first}`;

        const indexes = segments.get(key) ?? [];

        indexes.push(pathIndex);

        segments.set(key, indexes);
      }
    });

    const offsets = paths.map((path, pathIndex) => {
      const horizontal = new Set<number>();

      const vertical = new Set<number>();

      for (let i = 0; i < path.cells.length - 1; i++) {
        const a = path.cells[i];

        const b = path.cells[i + 1];

        const first = `${a[0]},${a[1]}`;

        const second = `${b[0]},${b[1]}`;

        const key =
          first < second ? `${first}-${second}` : `${second}-${first}`;

        const indexes = segments.get(key) ?? [];

        if (a[0] === b[0]) {
          indexes.forEach((index) => {
            horizontal.add(index);
          });
        } else {
          indexes.forEach((index) => {
            vertical.add(index);
          });
        }
      }

      const getOffset = (indexes: Set<number>) => {
        const array = Array.from(indexes).sort((a, b) => a - b);

        const position = array.indexOf(pathIndex);

        const center = (array.length - 1) / 2;

        return (position - center) * cellSize * 0.16;
      };

      return {
        x: getOffset(vertical),
        y: getOffset(horizontal),
      };
    });
    if (!isGameStarted) {
      return;
    }
    /*
     * Подсветка начальных ячеек.
     */
    paths.forEach((path, pathIndex) => {
      const [row, col] = path.cells[0];

      const x = borderSize / 2 + col * cellSize;
      const y = borderSize / 2 + row * cellSize;

      ctx.fillStyle = COLORS[pathIndex % COLORS.length];
      ctx.globalAlpha = 0.15;

      ctx.fillRect(x, y, cellSize, cellSize);

      ctx.globalAlpha = 1;
    });
    /*
     * Номера.
     */
    paths.forEach((path, pathIndex) => {
      const [row, col] = path.cells[0];

      const x = borderSize / 2 + col * cellSize + cellSize / 2;

      const y = borderSize / 2 + row * cellSize + cellSize / 2;

      ctx.fillStyle = COLORS[pathIndex % COLORS.length];

      // Цифра заметнее заливки клетки
      ctx.globalAlpha = 0.5;

      ctx.font = `bold ${cellSize * 0.8}px Arial`;

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillText(String(pathIndex + 1), x, y);

      ctx.globalAlpha = 1;
    });
    /*
     * Линии.
     */
    paths.forEach((path, pathIndex) => {
      if (path.cells.length < 2) {
        return;
      }

      const offset = offsets[pathIndex];

      ctx.strokeStyle = COLORS[pathIndex % COLORS.length];
      ctx.lineWidth = cellSize * 0.08;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.beginPath();

      path.cells.forEach(([row, col], index) => {
        let x = borderSize / 2 + col * cellSize + cellSize / 2 + offset.x;

        let y = borderSize / 2 + row * cellSize + cellSize / 2 + offset.y;

        // Первая точка — начинаем от границы кружка
        if (index === 0) {
          const [nextRow, nextCol] = path.cells[1];

          const dx = nextCol - col;
          const dy = nextRow - row;

          const radius = cellSize * 0.09;

          x += dx * radius;
          y += dy * radius;

          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();
    });

    /*
     * Точки начала.
     */
    paths.forEach((path, pathIndex) => {
      if (path.cells.length < 2) {
        return;
      }

      const [row, col] = path.cells[0];

      const offset = offsets[pathIndex];

      const x = borderSize / 2 + col * cellSize + cellSize / 2 + offset.x;

      const y = borderSize / 2 + row * cellSize + cellSize / 2 + offset.y;

      ctx.beginPath();

      ctx.strokeStyle = COLORS[pathIndex % COLORS.length];

      ctx.lineWidth = cellSize * 0.04;

      ctx.arc(x, y, cellSize * 0.06, 0, Math.PI * 2);

      ctx.stroke();
    });
  }, [rows, columns, paths, isGameStarted]);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-test"
      style={{
        display: "block",
        background: "#fff",
      }}
    />
  );
};

export default WordSearchCanvas;
